import { Box, VStack, Text } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { FiUploadCloud } from 'react-icons/fi'
import { getParentFolder } from '@/utils/folderUtils'
import { useNavigation } from '@/hooks/useNavigation'
import { useFileDrop } from '@/hooks/useFileDrop'
import { useAppTranslation } from '@/contexts/LanguageContext'
import FolderHeader from './FileExplorer/FolderHeader'
import RootHeader from './FileExplorer/RootHeader'
import SubfoldersGrid from './FileExplorer/SubfoldersGrid'
import FileTable from './FileExplorer/FileTable'
import LoadingState from './FileExplorer/LoadingState'
import ErrorState from './FileExplorer/ErrorState'
import EmptyState from './FileExplorer/EmptyState'

interface FileExplorerProps {
  onUpload: () => void
  onUploadFolder: () => void
  onCreateFolder: () => void
  onFilesDropped?: (files: File[]) => void
  onFolderDropped?: (files: FileList) => void
}

export default function FileExplorer({
  onUpload,
  onUploadFolder,
  onCreateFolder,
  onFilesDropped,
  onFolderDropped,
}: FileExplorerProps) {
  const navigate = useNavigate()
  const { t } = useAppTranslation(['files', 'common'])

  // Get navigation state and actions from context
  const {
    selectedFolder,
    folderTree,
    folderFiles,
    isLoadingFiles,
    filesError,
    selectFolder,
    navigateToParent,
    updateFolder,
    deleteFolder,
    refreshFolderFiles,
    refreshFolderTree,
  } = useNavigation()

  const handleFileSelect = (file: any) => {
    navigate(`/preview/${file.id}`)
  }

  const handleBack = () => {
    if (selectedFolder) {
      navigateToParent()
    }
  }

  const handleFolderUpdate = (updatedFolder: any) => {
    updateFolder(updatedFolder.id, {
      name: updatedFolder.name,
      description: updatedFolder.description,
    })
  }

  const handleFolderDelete = (folderId: number) => {
    deleteFolder(folderId)
  }

  // Uploads are handed straight to the upload panel, which owns progress,
  // duplicate prompts and per-file errors from here on.
  const handleFilesDropped = (files: File[]) => {
    if (!onFilesDropped) {
      onUpload()
      return
    }

    onFilesDropped(files)
  }

  const handleFolderDropped = (files: FileList) => {
    if (!onFolderDropped) {
      onUpload()
      return
    }

    onFolderDropped(files)
  }

  // Configure drag and drop
  const { isDragActive, isDragOver, dragProps } = useFileDrop({
    onFilesDropped: handleFilesDropped,
    onFolderDropped: handleFolderDropped,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/*': ['.txt', '.csv'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'],
    },
  })

  const getBackButtonText = () => {
    if (!selectedFolder) return t('buttons.back', { ns: 'common' })

    const parentFolder = getParentFolder(folderTree, selectedFolder)
    return parentFolder
      ? t('navigation.backTo', { ns: 'common', name: parentFolder.name })
      : t('navigation.backToAllDocuments', { ns: 'common' })
  }

  const hasSubfolders = selectedFolder
    ? (selectedFolder.subfolders && selectedFolder.subfolders.length > 0) ||
      (selectedFolder.children && selectedFolder.children.length > 0)
    : folderTree.length > 0

  // Show file explorer for both root (All Documents) and specific folders
  return (
    <Box
      p={{ base: 4, md: 8 }}
      pt={{ base: 2, md: 4 }}
      position="relative"
      flex="1"
      {...dragProps}
      style={{
        transition: 'all 0.2s ease',
        backgroundColor: isDragOver ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
        border: isDragOver ? '2px dashed #3b82f6' : '2px dashed transparent',
      }}
    >
      {/* Drag Overlay */}
      <AnimatePresence>
        {isDragActive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '12px',
              // border: '2px dashed #3b82f6',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="xl"
              textAlign="center"
              border="1px solid"
              borderColor="blue.200"
            >
              <VStack spacing={3}>
                <Box color="blue.500" fontSize="3xl">
                  <FiUploadCloud />
                </Box>
                <Text fontSize="lg" fontWeight="semibold" color="blue.600">
                  {t('upload.dropToUpload', { ns: 'files' })}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {selectedFolder
                    ? t('upload.toFolder', { ns: 'files', folder: selectedFolder.name })
                    : t('upload.toAllDocuments', { ns: 'files' })}
                </Text>
              </VStack>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <VStack align="stretch">
        {/* Back Button - only show when not in root */}
        {/* {selectedFolder && (
          <MotionBox
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              leftIcon={<FiArrowLeft />}
              variant="ghost"
              size="sm"
              onClick={handleBack}
              mb={2}
              _hover={{
                bg: 'gray.100',
                transform: 'translateX(-2px)',
              }}
              transition="all 0.2s"
            >
              {getBackButtonText()}
            </Button>
          </MotionBox>
        )} */}

        {/* Header - show for specific folders only */}
        {selectedFolder && (
          <FolderHeader
            folder={selectedFolder}
            onUpload={onUpload}
            onCreateFolder={onCreateFolder}
            onFolderUpdate={handleFolderUpdate}
            onFolderDelete={handleFolderDelete}
            onFilesChange={refreshFolderFiles}
            onFolderTreeChange={refreshFolderTree}
          />
        )}

        {/* Header - the root ("All Documents") equivalent of FolderHeader */}
        {!selectedFolder && (
          <RootHeader
            onUpload={onUpload}
            onUploadFolder={onUploadFolder}
            onCreateFolder={onCreateFolder}
            onFilesChange={refreshFolderFiles}
            onFolderTreeChange={refreshFolderTree}
          />
        )}

        {/* Content heading + subfolders. Shown whenever there is anything to
            list, so the heading survives folders-only and files-only views. */}
        {!isLoadingFiles && !filesError && (hasSubfolders || folderFiles.length > 0) && (
          <SubfoldersGrid
            subfolders={
              selectedFolder
                ? selectedFolder.subfolders || selectedFolder.children || []
                : folderTree
            }
            onFolderSelect={selectFolder}
            fileCount={folderFiles.length}
          />
        )}

        {/* Files Content */}
        <Box>
          {isLoadingFiles ? (
            <LoadingState />
          ) : filesError ? (
            <ErrorState error={filesError} />
          ) : folderFiles.length === 0 && !hasSubfolders ? (
            <EmptyState onUpload={onUpload} />
          ) : Boolean(folderFiles.length) ? (
            <FileTable
              files={folderFiles}
              preferenceScope="explorer"
              resetKey={selectedFolder?.id ?? 'root'}
            />
          ) : null}
        </Box>
      </VStack>
    </Box>
  )
}
